import { useMemo } from "react";
import { Button } from "@/components/ui/button";

interface ContextualAction {
  label: string;
  prompt: string;
}

interface ContextualActionsProps {
  companyId?: string;
  companyName?: string;
  onAction: (prompt: string) => void;
  disabled?: boolean;
}

export function ContextualActions({ companyId, companyName, onAction, disabled }: ContextualActionsProps) {
  const actions = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    
    const contextActions: ContextualAction[] = [];

    if (companyId) {
      // Company-specific actions
      contextActions.push({
        label: "📊 Status",
        prompt: "What's the current KPI status?",
      });
      contextActions.push({
        label: "🔴 Focus areas",
        prompt: "What needs my attention?",
      });

      // Time-aware: Friday-Sunday = report time
      if (dayOfWeek >= 5 || dayOfWeek === 0) {
        contextActions.push({
          label: "📋 Weekly report",
          prompt: "Generate this week's report",
        });
      } else {
        contextActions.push({
          label: "📈 Compare WoW",
          prompt: "How did we do this week vs last?",
        });
      }

      contextActions.push({
        label: "⚡ Submit KPIs",
        prompt: "I want to submit KPI values",
      });

      contextActions.push({
        label: "💡 Suggestions",
        prompt: "What should I focus on this week?",
      });
    } else {
      // No company context
      contextActions.push({
        label: "📊 Portfolio",
        prompt: "How are all my companies doing?",
      });
      contextActions.push({
        label: "🎯 Focus",
        prompt: "What should I focus on this week?",
      });
      contextActions.push({
        label: "📁 Companies",
        prompt: "List my companies",
      });
      contextActions.push({
        label: "➕ New company",
        prompt: "Help me create a new company",
      });
    }

    return contextActions.slice(0, 4);
  }, [companyId, companyName]);

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      {actions.map((action) => (
        <Button
          key={action.label}
          variant="outline"
          size="sm"
          className="flex-shrink-0 text-xs rounded-full border-border/50 hover:bg-card hover:border-primary/30 h-8 px-3"
          onClick={() => onAction(action.prompt)}
          disabled={disabled}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}
